/**
 * Validation patterns shared by the energy-flow detail steps (PersonalDetails,
 * ContactDetails and the consolidated CustomerDetails). Kept here as a single
 * source of truth so the three steps validate identically — important for the
 * exp-n-477 A/B test, where B/C must behave exactly like control A.
 *
 * These deliberately differ from the app-wide `@eneco/common/validation`
 * regexes: the flow accepts space separators in phone numbers and rejects
 * country codes (see the "Zonder landcode" copy), and the name check is lenient
 * on purpose. Do not swap them for the common ones without a product decision.
 */

/**
 * Any single-line value containing at least one letter, digit or space — rejects
 * punctuation-only input like "!!!". Simplified from the original
 * `^(?=.*[\w\s])[^\w]*.*$`: the `[^\w]*` was redundant with `.*` and caused
 * super-linear backtracking (SonarQube). Behaviour is unchanged for all
 * single-line input (verified against the previous pattern).
 */
export const NAME_PATTERN = /^(?=.*[\w\s]).*$/;

/** Like {@link NAME_PATTERN} but also allows an empty value (optional field). */
export const OPTIONAL_NAME_PATTERN = /^(?:$|(?=.*[\w\s]).*)$/;

/**
 * Dutch landline/mobile number: 10 digits with an optional single separator, no
 * country code. Rewritten from three self-anchored alternatives into one
 * `^(?:…)$` group with `\d` for clearer precedence and lower complexity
 * (SonarQube); matches exactly the same numbers as before.
 */
export const DUTCH_PHONE_PATTERN = /^(?:0[0-57-9]\d{2}[-\s]?\d{6}|0[0-57-9]\d[-\s]?\d{7}|06[-\s]?\d{8})$/;
